import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {observer} from 'mobx-react';
import s from '../s';
import ProductRender from '../rendering/render';
import {FrameBuilder} from '../../renderV2/frame-builder/frame-builder';
import {buildProductSlides, pickZoomVideoSrc, webpSrc, type ProductSlide} from '../components/slider/product-slides/product-slides';
import {productSlidesInput} from '../components/slider/product-slides/product-slides-input';
import {screenResizeStore} from '../../global/store/screen-resize-store';
import FavoriteButton from './favorite-button';
import {isRedesignPhone} from './viewport';

/** Before the first measurement the frame box takes the render's own proportions (the server shell reserves the same box). */
const frameAspect = (): string | undefined => {
    const css = s.ps.css;
    if (!css || !(css.frame_display_width > 0) || !(css.frame_display_height > 0)) return undefined;
    return `${css.frame_display_width} / ${css.frame_display_height}`;
};

/**
 * The live frame (the control's #render content: the same renderers, the same pcs), scaled down to fit the gallery. The frame is
 * drawn at the size the product page always draws it (pcs, and with it the add-to-cart body, do not change) and shown through a
 * CSS scale, so the box is the frame's own aspect at the gallery's width.
 */
const LiveFrame: React.FC = observer(() => {
    const outer = useRef<HTMLDivElement>(null);
    const inner = useRef<HTMLDivElement>(null);
    const [fit, setFit] = useState<{scale: number; aspect: string} | null>(null);

    useLayoutEffect(() => {
        const box = outer.current;
        const frame = inner.current;
        if (!box || !frame) return;
        const measure = () => {
            const width = frame.offsetWidth;
            const height = frame.offsetHeight;
            if (width <= 0 || height <= 0) return;
            const scale = Math.min(1, box.clientWidth / width);
            const aspect = `${width} / ${height}`;
            setFit(prev => (prev && Math.abs(prev.scale - scale) < 0.0005 && prev.aspect === aspect ? prev : {scale, aspect}));
        };
        measure();
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(measure);
        observer.observe(box);
        observer.observe(frame);
        return () => observer.disconnect();
    }, []);

    const aspect = fit?.aspect ?? frameAspect();

    return (
        <div
            ref={outer}
            className="pp3-frame"
            style={aspect ? ({'--pp3-frame-aspect': aspect} as React.CSSProperties) : undefined}
        >
            <div
                id="render"
                ref={inner}
                className="pp3-frame-inner"
                style={{transform: `translateX(-50%) scale(${fit?.scale ?? 1})`}}
            >
                {s.ready &&
                    (s.RenderingV2 ? (
                        <FrameBuilder
                            frame={s.frameV2}
                            pcs={s.pcs}
                        />
                    ) : (
                        <ProductRender
                            zoom={false}
                            pcs={s.pcs}
                            css={s.pcs.css}
                        />
                    ))}
            </div>
        </div>
    );
});

/** A gallery slide: the live frame, a photo, or a video. */
const SlideView: React.FC<{slide: ProductSlide}> = observer(({slide}) => {
    const zoom = slide.zoom;
    if (zoom.type === 'product-render') {
        return <LiveFrame />;
    }
    if (zoom.type === 'video') {
        return (
            <video
                className="pp3-slide-media"
                controls
                playsInline
                preload="metadata"
                poster={zoom.poster}
            >
                <source
                    src={pickZoomVideoSrc(zoom, window.innerWidth, window.innerHeight)}
                    type="video/mp4"
                />
            </video>
        );
    }
    return (
        <div
            className="pp3-slide-photo"
            style={zoom.backgroundColor ? {backgroundColor: zoom.backgroundColor} : undefined}
        >
            <picture>
                <source
                    type="image/webp"
                    srcSet={webpSrc(zoom.src)}
                />
                <img
                    className="pp3-slide-media"
                    src={zoom.src}
                    alt={zoom.alt}
                    loading="lazy"
                />
            </picture>
            {zoom.overlay ? <span className="pp3-slide-overlay">{zoom.overlay}</span> : null}
        </div>
    );
});

const Thumb: React.FC<{slide: ProductSlide; selected: boolean; onSelect: () => void}> = ({slide, selected, onSelect}) => (
    <button
        type="button"
        className={`pp3-thumb${selected ? ' selected' : ''}${slide.thumb.border ? ' framed' : ''}`}
        aria-label={slide.accessibleName.replace('Open larger image of', 'Show')}
        aria-pressed={selected}
        onClick={onSelect}
        style={slide.thumb.backgroundImage ? {backgroundImage: slide.thumb.backgroundImage} : undefined}
    >
        <picture>
            <source
                type="image/webp"
                srcSet={webpSrc(slide.thumb.url)}
            />
            <img
                src={slide.thumb.url}
                alt=""
                loading="lazy"
                style={slide.thumb.imgStyle}
            />
        </picture>
        {slide.thumb.showPlayOverlay && (
            <span
                className="pp3-thumb-play"
                aria-hidden="true"
            />
        )}
    </button>
);

/**
 * The redesign's gallery: SliderTH's slides (product-slides.ts), the first one the live frame. Desktop shows the picked slide over a
 * row of 72px thumbnails; phones swipe through the slides with a "Photo N of M" counter. The wish-list heart sits on the stage.
 */
const Gallery: React.FC = observer(() => {
    const slides = s.ready ? buildProductSlides(productSlidesInput()) : [];
    const [pickedId, setPickedId] = useState<string | null>(null);
    const [phoneIndex, setPhoneIndex] = useState(0);
    const track = useRef<HTMLDivElement>(null);
    const phone = isRedesignPhone(screenResizeStore.width);

    // the live frame always exists as the first slide, even before the product thumbnail is known
    const items: ProductSlide[] = slides.length > 0 && slides[0].zoom.type === 'product-render' ? slides : [{id: 'slide-prod', kind: 'product', description: 'product', accessibleName: 'Show product', thumb: {url: '', alt: 'Product'}, zoom: {type: 'product-render'}}, ...slides];
    const picked = items.find(slide => slide.id === pickedId) ?? items[0];

    useEffect(() => {
        if (phoneIndex >= items.length) setPhoneIndex(0);
    }, [items.length, phoneIndex]);

    const onTrackScroll = () => {
        const el = track.current;
        if (!el || el.clientWidth <= 0) return;
        setPhoneIndex(Math.max(0, Math.min(items.length - 1, Math.round(el.scrollLeft / el.clientWidth))));
    };

    if (phone) {
        return (
            <div
                className="pp3-gallery"
                aria-roledescription="carousel"
                role="region"
                aria-label="Product photos"
            >
                <div className="pp3-stage">
                    <div
                        className="pp3-track"
                        ref={track}
                        onScroll={onTrackScroll}
                        tabIndex={0}
                        aria-label="Swipe for more photos"
                    >
                        {items.map((slide, i) => (
                            <div
                                key={slide.id}
                                className="pp3-track-slide"
                                role="group"
                                aria-roledescription="slide"
                                aria-label={`${i + 1} of ${items.length}`}
                            >
                                <SlideView slide={slide} />
                            </div>
                        ))}
                    </div>
                    <FavoriteButton />
                    {items.length > 1 && (
                        <span
                            className="pp3-counter"
                            aria-live="polite"
                        >
                            Photo {Math.min(phoneIndex, items.length - 1) + 1} of {items.length}
                        </span>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="pp3-gallery">
            <div className="pp3-stage">
                <SlideView slide={picked} />
                <FavoriteButton />
            </div>
            {items.length > 1 && (
                <div
                    className="pp3-thumbs"
                    role="group"
                    aria-label="Product photos"
                >
                    {items.map(slide => (
                        <Thumb
                            key={slide.id}
                            slide={slide}
                            selected={slide.id === picked.id}
                            onSelect={() => setPickedId(slide.id)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
});

export default Gallery;
